import React, { useState, useEffect, useCallback } from 'react';
import { getFriends, sendFriendRequest, acceptFriendRequest, declineFriendRequest, getPendingRequests, sendAppInvitation, removeFriend } from '../actions/friends';
import { Typography, TextField, Button, List, ListItem, ListItemText, IconButton, Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Alert, AlertTitle, Card, CardContent, useTheme, Avatar, Chip, Tooltip } from '@mui/material';
import { Add as AddIcon, Check as CheckIcon, Close as CloseIcon, Person as PersonIcon, PersonAdd as PersonAddIcon, Email as EmailIcon, Info as InfoIcon, Share as ShareIcon, PersonRemove as PersonRemoveIcon } from '@mui/icons-material';
import { useAlert } from '../contexts/AlertContext';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';

const FriendsPage = () => {
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [newFriendEmail, setNewFriendEmail] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
  const [invitationEmail, setInvitationEmail] = useState('');
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [friendToRemove, setFriendToRemove] = useState(null);
  const { showAlert } = useAlert();
  const theme = useTheme();

  const fetchFriendsAndRequests = useCallback(async () => {
    try {
      const friendData = await getFriends();
      setFriends(friendData);

      const pendingData = await getPendingRequests();
      setPendingRequests(pendingData);
    } catch (error) {
      showAlert('Error fetching friends and requests', 'error');
    }
  }, [showAlert]);

  useEffect(() => {
    fetchFriendsAndRequests();
  }, [fetchFriendsAndRequests]);

  const handleAddFriend = async () => {
    try {
      await sendFriendRequest(newFriendEmail);
      setNewFriendEmail('');
      fetchFriendsAndRequests();
      setDialogOpen(false);
      showAlert('Friend request sent successfully!', 'success');
    } catch (error) {
      // Get the server error message
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
      
      // Check if the error indicates the user is not registered
      if (errorMessage?.includes('not found') || errorMessage?.includes('not registered') || errorMessage?.includes('User not found')) {
        // Show the error message first
        showAlert(errorMessage, 'error');
        // Then offer the invitation option
        setInvitationEmail(newFriendEmail);
        setDialogOpen(false);
        setInvitationDialogOpen(true);
      } else {
        // Show server-specific error message
        showAlert(errorMessage || 'Error sending friend request', 'error');
      }
    }
  };

  const handleAcceptFriend = async (friendId) => {
    try {
      await acceptFriendRequest(friendId);
      fetchFriendsAndRequests();
      showAlert('Friend request accepted!', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error accepting friend request';
      showAlert(errorMessage, 'error');
    }
  };

  const handleDeclineFriend = async (friendId) => {
    try {
      await declineFriendRequest(friendId);
      fetchFriendsAndRequests();
      showAlert('Friend request declined', 'info');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error declining friend request';
      showAlert(errorMessage, 'error');
    }
  };

  const handleSendInvitation = async () => {
    setSendingInvitation(true);
    try {
      await sendAppInvitation(invitationEmail);
      setInvitationEmail('');
      setInvitationDialogOpen(false);
      showAlert('App invitation sent successfully! Your friend will receive an email to join the app.', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error sending invitation';
      showAlert(errorMessage, 'error');
    } finally {
      setSendingInvitation(false);
    }
  };

  const handleRemoveFriend = async () => {
    try {
      await removeFriend(friendToRemove.id);
      setRemoveDialogOpen(false);
      setFriendToRemove(null);
      fetchFriendsAndRequests();
      showAlert('Friend removed successfully', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error removing friend';
      showAlert(errorMessage, 'error');
    }
  };

  const handleRemoveFriendClick = (friend) => {
    setFriendToRemove(friend);
    setRemoveDialogOpen(true);
  };

  const capitalizeName = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 3 },
      minHeight: '100vh'
    }}>
      {/* Add Friend Button */}
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        justifyContent: { xs: 'center', md: 'flex-start' },
        mt: { xs: 2, sm: 0 },
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Tooltip title="Send a friend request to someone with an account" arrow>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ 
              borderRadius: 3,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 6px 25px rgba(0, 0, 0, 0.4)'
                  : '0 6px 25px rgba(0, 0, 0, 0.15)',
              }
            }}
          >
            Add Friend
          </Button>
        </Tooltip>
        
        <Tooltip title="Invite someone to join the app" arrow>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ShareIcon />}
            onClick={() => {
              setInvitationEmail('');
              setInvitationDialogOpen(true);
            }}
            sx={{ 
              borderRadius: 3,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              borderWidth: 2,
              '&:hover': {
                transform: 'translateY(-2px)',
                borderWidth: 2,
              }
            }}
          >
            Invite Friends
          </Button>
        </Tooltip>
      </Box>
      
      {/* Pending Friend Requests Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Pending Requests
        </Typography>
        
        {/* Pending Friend Requests Alert - Better display for desktop */}
        {pendingRequests.length > 0 && (
          <Card sx={{
            mb: 4,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(33, 150, 243, 0.1)'
              : 'rgba(33, 150, 243, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: `1px solid ${theme.palette.info.main}30`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.2)'
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonAddIcon sx={{ mr: 2, color: theme.palette.info.main }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                      You have {pendingRequests.length} pending friend request{pendingRequests.length > 1 ? 's' : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Review and respond to friend requests below
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
        
        {pendingRequests.length === 0 ? (
          <Card sx={{
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.2)'
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.2)',
            p: 4,
            textAlign: 'center'
          }}>
            <Typography variant="body1" color="text.secondary">
              No pending friend requests.
            </Typography>
          </Card>
        ) : (
          <Box sx={cardGridSx(260, 3)}>
            {pendingRequests.map(request => (
              <Box key={request.id} sx={cardItemSx()}>
                <Card sx={{
                  height: '100%',
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                      : '0 8px 30px rgba(0, 0, 0, 0.15)',
                  }
                }}>
                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Avatar sx={{ 
                      width: 60, 
                      height: 60, 
                      mb: 2, 
                      bgcolor: theme.palette.primary.main,
                      fontSize: '1.5rem'
                    }}>
                      {request.name.charAt(0).toUpperCase()}
                    </Avatar>
                    
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {capitalizeName(request.name)}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, color: 'text.secondary', minWidth: 0 }}>
                      <EmailIcon sx={{ fontSize: 16, mr: 1 }} />
                      <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                        {request.email}
                      </Typography>
                    </Box>

                    <Chip 
                      label="Pending Request" 
                      size="small" 
                      sx={{ 
                        mb: 2,
                        backgroundColor: theme.palette.warning.main + '20',
                        color: theme.palette.warning.main,
                        fontWeight: 500
                      }} 
                    />
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                      <Tooltip title="Accept friend request" arrow>
                        <IconButton 
                          color="success" 
                          onClick={() => handleAcceptFriend(request.id)}
                          sx={{
                            bgcolor: theme.palette.success.main + '20',
                            '&:hover': {
                              bgcolor: theme.palette.success.main + '30',
                              transform: 'scale(1.1)',
                            }
                          }}
                        >
                          <CheckIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Decline friend request" arrow>
                        <IconButton 
                          color="error" 
                          onClick={() => handleDeclineFriend(request.id)}
                          sx={{
                            bgcolor: theme.palette.error.main + '20',
                            '&:hover': {
                              bgcolor: theme.palette.error.main + '30',
                              transform: 'scale(1.1)',
                            }
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Friends Section */}
      <Box>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          My Friends
        </Typography>
        
        {friends.length === 0 ? (
          <Card sx={{
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.2)'
              : '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.2)',
            p: 4,
            textAlign: 'center'
          }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No friends added yet.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click on "Add Friend" to send a new friend request.
            </Typography>
          </Card>
        ) : (
          <Box sx={cardGridSx(260, 3)}>
            {friends.map(friend => (
              <Box key={friend.id} sx={cardItemSx()}>
                <Card sx={{
                  height: '100%',
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                    : '0 4px 20px rgba(0, 0, 0, 0.1)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                      : '0 8px 30px rgba(0, 0, 0, 0.15)',
                  }
                }}>
                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Avatar sx={{ 
                      width: 60, 
                      height: 60, 
                      mb: 2, 
                      bgcolor: theme.palette.secondary.main,
                      fontSize: '1.5rem'
                    }}>
                      {friend.name.charAt(0).toUpperCase()}
                    </Avatar>
                    
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {capitalizeName(friend.name)}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, color: 'text.secondary', minWidth: 0 }}>
                      <EmailIcon sx={{ fontSize: 16, mr: 1 }} />
                      <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                        {friend.email}
                      </Typography>
                    </Box>

                    {friend.status === 'pending' && 
                      <Chip 
                        label={'Pending'} 
                        size="small" 
                        sx={{ 
                          backgroundColor: theme.palette.warning.main + '20',
                          color: theme.palette.warning.main,
                          fontWeight: 500
                        }} 
                    />}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {friend.status === 'pending' ? (
                        <Tooltip title="Accept friend request" arrow>
                          <IconButton 
                            color="primary" 
                            onClick={() => handleAcceptFriend(friend.id)}
                            sx={{
                              bgcolor: theme.palette.primary.main + '20',
                              '&:hover': {
                                bgcolor: theme.palette.primary.main + '30',
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Remove friend" arrow>
                          <IconButton 
                            color="error" 
                            onClick={() => handleRemoveFriendClick(friend)}
                            sx={{
                              bgcolor: theme.palette.error.main + '20',
                              '&:hover': {
                                bgcolor: theme.palette.error.main + '30',
                                transform: 'scale(1.1)',
                              }
                            }}
                          >
                            <PersonRemoveIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Add Friend Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(30, 30, 30, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <PersonAddIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Add a Friend
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Account requirement info */}
          <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 2,
            mb: 3,
            mt: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(33, 150, 243, 0.1)'
              : 'rgba(33, 150, 243, 0.05)',
            border: `1px solid ${theme.palette.info.main}30`
          }}>
            <InfoIcon sx={{ 
              color: theme.palette.info.main, 
              mt: 0.5,
              fontSize: 20
            }} />
            <Box>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                color: theme.palette.info.main,
                mb: 1,
              }}>
                Account Required
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Your friend must already have an Event Planner account to receive friend requests. Make sure they've signed up for the app first.
              </Typography>
            </Box>
          </Box>

          <TextField
            label="Friend's Email"
            value={newFriendEmail}
            onChange={(e) => setNewFriendEmail(e.target.value)}
            variant="outlined"
            fullWidth
            margin="normal"
            type="email"
            placeholder="Enter your friend's email address"
            helperText="Enter the email address they used to create their account"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />

          {/* Invite Friends Option */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            mt: 3,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(76, 175, 80, 0.1)'
              : 'rgba(76, 175, 80, 0.05)',
            border: `1px solid ${theme.palette.success.main}30`
          }}>
            <ShareIcon sx={{ 
              color: theme.palette.success.main, 
              fontSize: 20
            }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                color: theme.palette.success.main,
                mb: 0.5,
              }}>
                Friend not registered?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                If your friend doesn't have an account yet, you can invite them to join the app.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="success"
              size="small"
              startIcon={<ShareIcon />}
              onClick={() => {
                setInvitationEmail(newFriendEmail);
                setDialogOpen(false);
                setInvitationDialogOpen(true);
              }}
              disabled={!newFriendEmail}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                }
              }}
            >
              Invite
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddFriend}
            variant="contained"
            disabled={!newFriendEmail}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invitation Dialog */}
      <Dialog 
        open={invitationDialogOpen} 
        onClose={() => setInvitationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(30, 30, 30, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <ShareIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Invite Friend to Join
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* Invitation info */}
          <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 2,
            mb: 3,
            mt: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(76, 175, 80, 0.1)'
              : 'rgba(76, 175, 80, 0.05)',
            border: `1px solid ${theme.palette.success.main}30`
          }}>
            <InfoIcon sx={{ 
              color: theme.palette.success.main, 
              mt: 0.5,
              fontSize: 20
            }} />
            <Box>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                color: theme.palette.success.main,
                mb: 1,
              }}>
                Send Invitation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Your friend doesn't have an account yet. Send them an invitation to join the Event Planner app so you can connect and share events together.
              </Typography>
            </Box>
          </Box>

          <TextField
            label="Friend's Email"
            value={invitationEmail}
            onChange={(e) => setInvitationEmail(e.target.value)}
            variant="outlined"
            fullWidth
            margin="normal"
            type="email"
            placeholder="Enter your friend's email address"
            helperText="We'll send them an invitation to join the app"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setInvitationDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendInvitation}
            variant="contained"
            disabled={!invitationEmail || sendingInvitation}
            startIcon={sendingInvitation ? null : <ShareIcon />}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            {sendingInvitation ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Friend Confirmation Dialog */}
      <Dialog 
        open={removeDialogOpen} 
        onClose={() => setRemoveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(30, 30, 30, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          pb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.error.main
        }}>
          <PersonRemoveIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Remove Friend
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
            Are you sure you want to remove <strong>{friendToRemove?.name}</strong> from your friends list?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. You'll need to send a new friend request to reconnect.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={() => setRemoveDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRemoveFriend}
            variant="contained"
            color="error"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Remove Friend
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FriendsPage;
